import React, { PropsWithChildren, useContext } from "react";

import { Translatable, Translation } from "../../translation";
import Button, { ButtonPropsProvider } from "../Button";
import { dialogContext } from "../Dialog";
import { ActionGroup } from "./ActionGroup";
import ButtonSet from "./ButtonSet";
import styles from "./styles.module.scss";

export type SectionHeaderProps = PropsWithChildren<{
  title: Translatable;
  titleClass?: string;
  visuallyHideTitle?: boolean;
  className?: string;
}>;

/** The 'header' component for things other than the entire page — so, the headers at the top of cards, between cards, at the top of modals, etc */
export function SectionHeader({ title, titleClass, visuallyHideTitle, children, className = "" }: SectionHeaderProps) {
  const { dialogId, onClickClose, titleRef } = useContext(dialogContext);
  return (
    <header className={`${styles.Header} ${className}`}>
      <div className={styles.TitleWithActionButtons}>
        <h2
          tabIndex={-1}
          id={dialogId ? `modal-${dialogId}-title` : undefined}
          className={`
            ${titleClass ?? "ds-h3"}
            ds-m-0
            ${styles.Title}
            ${visuallyHideTitle ? "is-sr-only" : ""}
          `}
          ref={titleRef ?? undefined}
        >
          <Translation props={title} />
        </h2>
        <div>
          <ActionGroup className={styles.ActionButtons}>{children}</ActionGroup>
        </div>
      </div>

      {onClickClose ? (
        <Button
          iconButton
          icon="xmarkLarge"
          label="Close"
          onClick={onClickClose}
          className={styles.CloseButton}
          testId="dialog-close"
          size="small"
        />
      ) : null}
    </header>
  );
}

/** A simple footer component — mostly its job is to add a bit of padding and  */
export function SectionFooter({ children, className }: PropsWithChildren<{ className?: string }>) {
  return <footer className={`${styles.Footer} ${className ?? ""}`}>{children}</footer>;
}

/** A SectionFooter with an end-aligned ButtonSet in it — this comes up loads at the bottom of forms (you'd either just put <Button label="Cancel" /><Button label="Save" /> in it, or else <Button label="Delete" /><Spacer /><Button label="Save" />) so it made sense to make a little shorthand function for it */
export function StandardFormFooter({ children, className }: PropsWithChildren<{ className?: string }>) {
  return (
    <SectionFooter className={className}>
      <ButtonSet align="end">{children}</ButtonSet>
    </SectionFooter>
  );
}

/** A StandardToolbar is the component we use for, eg, the date picker and button at the top of "At A Glance" */
export function StandardToolbar({ children, className }: PropsWithChildren<{ className?: string }>) {
  return (
    <ButtonSet align="space-between" className={`ds-mb-5 ${className ?? ""}`}>
      <ButtonPropsProvider size="medium">{children}</ButtonPropsProvider>
    </ButtonSet>
  );
}
